import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import IProjectGroupsCompletionItem from "../dataTypes/IProjectGroupsCompletionItem";
import Trophy from "../../../common/components/Trophy/Trophy";

interface EnduserParcoursStatusCardProps {
  parcours: IProjectGroupsCompletionItem;
  onClick: () => void;
}

const EnduserParcoursStatusCard = ({ parcours, onClick }: EnduserParcoursStatusCardProps) => {
  const { t, i18n } = useTranslation();

  // Calculate completion status using progressPercentageOnLatestBuild from API
  const { status, progress, statusBgColor, statusColor, isCompleted, statusKey } = useMemo(() => {
    const progress = Math.round(parcours.progressPercentageOnLatestBuild ?? 0);

    if (progress === 100) {
      return {
        status: t("enduserStats.status.completed"),
        progress,
        statusBgColor: "bg-green-500",
        statusColor: "text-white",
        isCompleted: true,
        statusKey: "completed" as const,
      };
    }
    if (progress > 0) {
      return {
        status: t("enduserStats.status.inProgress"),
        progress,
        statusBgColor: "bg-yellow-500",
        statusColor: "text-white",
        isCompleted: false,
        statusKey: "inProgress" as const,
      };
    }
    return {
      status: t("enduserStats.status.notStarted"),
      progress: 0,
      statusBgColor: "bg-gray-100",
      statusColor: "text-gray-600",
      isCompleted: false,
      statusKey: "notStarted" as const,
    };
  }, [parcours, t]);

  // Format last activity date
  const lastActivity = useMemo(() => {
    if (!parcours.dateLastSession) return null;
    const locale = i18n.language === "fr" ? fr : undefined;
    return formatDistanceToNow(new Date(parcours.dateLastSession), { addSuffix: true, locale });
  }, [parcours.dateLastSession, i18n.language]);

  // Use projectGroupThumbnail from API (this is what the backend returns)
  const thumbnailUrl = parcours.projectGroupThumbnail?.s3_url;

  return (
    <div
      onClick={onClick}
      className="relative group cursor-pointer rounded-xl overflow-hidden shadow hover:shadow-lg transition-all hover:scale-[1.02]"
      style={{ width: "180px", height: "120px" }}
    >
      {/* Background image or gradient */}
      {thumbnailUrl ? (
        <img
          src={thumbnailUrl}
          alt={parcours.projectGroupName}
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-slate-600 via-slate-700 to-slate-800">
          {/* Subtle decorative pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-2 right-8 w-12 h-12 border-2 border-white rounded-full" />
            <div className="absolute bottom-8 left-3 w-6 h-6 border-2 border-white rounded-full" />
          </div>
        </div>
      )}

      {/* Dark overlay for text readability - stronger gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />

      {/* Content */}
      <div className="absolute inset-0 p-3 flex flex-col justify-end">
        {/* Name */}
        <h3
          className="text-white font-semibold text-sm leading-tight line-clamp-2 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]"
          title={parcours.projectGroupName}
        >
          {parcours.projectGroupName}
        </h3>
        {/* Last activity subtitle */}
        {lastActivity && (
          <span className="text-white/70 text-[10px] mt-0.5 drop-shadow-[0_1px_1px_rgba(0,0,0,0.6)]">
            {lastActivity}
          </span>
        )}
      </div>

      {/* Status badge with icon */}
      <div className="absolute top-2 left-2">
        <span
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 ${statusBgColor} ${statusColor} text-[10px] font-medium rounded-lg shadow`}
        >
          {/* Trophy for perfect score, checkmark for completed, clock for in progress, dots for not started */}
          {isCompleted && parcours.hasPerfectScoreOnLatestBuild ? (
            <Trophy width={12} height={12} fill="#FFFFFF" />
          ) : isCompleted ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-3 w-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={3}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          ) : statusKey === "inProgress" ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-3 w-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-3 w-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          )}
          {status}
        </span>
      </div>

      {/* Progress indicator (hidden when 100% since "Terminé" badge already indicates completion) */}
      {progress > 0 && progress < 100 && (
        <div className="absolute top-2 right-2">
          <span className="inline-flex items-center justify-center min-w-[1.75rem] h-6 px-1.5 bg-white/90 text-gray-800 text-xs font-bold rounded-lg shadow">
            {progress}%
          </span>
        </div>
      )}

      {/* Progress bar at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/30">
        <div
          className={`h-full transition-all duration-300 ${
            progress === 100 ? "bg-green-500" : progress > 0 ? "bg-yellow-500" : "bg-transparent"
          }`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

export default EnduserParcoursStatusCard;
